import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import './Checkbox.css';

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: ReactNode;
  /** Visual-only tri-state marker for "some selected" in tables. */
  indeterminate?: boolean;
}

/**
 * Accessible checkbox built on a visually-hidden native input so keyboard and
 * screen-reader behavior is free. The painted box reflects checked and
 * indeterminate states. `indeterminate` is presentational (header select-all).
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, indeterminate = false, className, id, disabled, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;

  return (
    <span className={cn('checkbox', disabled && 'checkbox--disabled', className)}>
      {/* O controle envolve input + caixa para que o input transparente cubra a
          área de toque de 24px (WCAG 2.5.8 / axe target-size) por cima da caixa
          pintada de 18px. */}
      <span className="checkbox__control">
        <input
          ref={ref}
          id={inputId}
          type="checkbox"
          className="checkbox__input"
          disabled={disabled}
          aria-checked={indeterminate ? 'mixed' : undefined}
          {...rest}
        />
        {/* A LABEL, not a span: the painted box is where people click, and a span
            beside a visually-hidden input is inert — the same defect the ad Switch
            had on its track. `aria-hidden` keeps it out of the accessible name,
            which the text label (or `aria-label`) already carries. */}
        <label htmlFor={inputId} className="checkbox__box" aria-hidden="true">
          {indeterminate ? (
            <Minus className="checkbox__mark" strokeWidth={3} />
          ) : (
            <Check className="checkbox__mark" strokeWidth={3} />
          )}
        </label>
      </span>
      {label != null && (
        <label htmlFor={inputId} className="checkbox__label">
          {label}
        </label>
      )}
    </span>
  );
});
